/* SemBot · forme dei dati del backend NestJS.
 *
 * Ricavate leggendo le entity e i controller in `src/modules/**` (sola
 * lettura). I nomi dei campi sono quelli esatti del server: non si rinominano
 * qui, altrimenti diventa impossibile confrontare una risposta con il codice
 * che la produce. La traduzione in italiano avviene nei componenti, non nei tipi.
 *
 * Molti campi sono `| null` per davvero: un backtest interrotto o un bot mai
 * partito hanno metriche assenti, e trattarle come zero produrrebbe verdetti
 * inventati.
 */

/* ---------- comuni ---------- */

export interface EntitaBase {
  id: number;
  createdAt: string;
  updatedAt: string;
}

/** Forma paginata di `@nestjsx/crud` quando si passa `limit` o `page`. */
export interface PaginaCrud<T> {
  data: T[];
  count: number;
  total: number;
  page: number;
  pageCount: number;
}

/* ---------- auth ---------- */

export interface CredenzialiAccesso {
  username: string;
  password: string;
}

/**
 * Il token è annidato due volte: `access_token.access_token`. Non è un errore
 * di trascrizione, è la forma reale della risposta del server e il frontend
 * attuale la legge già così.
 */
export interface RispostaAccesso {
  user: { id: number; username: string };
  access_token: { access_token: string };
}

export interface ProfiloUtente {
  username: string;
  /** Sempre `undefined`: il server firma `id` ma la strategia legge `sub`. */
  userId?: number;
}

/* ---------- exchange e account ---------- */

export interface Exchange extends EntitaBase {
  name: string;
  slug: string;
  isCCXT: boolean;
  /** Calcolato a runtime: `false` se lo slug non esiste più in ccxt. */
  isCCXTSupported: boolean;
}

export interface Account extends EntitaBase {
  name: string;
  description: string | null;
  exchangeId: number;
  exchange?: Exchange;
}

export interface MonetaSaldo {
  coin: string;
  balance: number;
  price: number | null;
  value: number | null;
}

export interface SaldoAccount {
  account: Account;
  /** Include anche le polveri. */
  total: number;
  coins: MonetaSaldo[];
  dust: { count: number; value: number };
  /** Messaggio già in italiano, pronto da mostrare accanto al conto. */
  error: string | null;
}

export interface SaldiTuttiAccount {
  total: number;
  balances: SaldoAccount[];
}

export interface IndirizzoDeposito {
  currency: string;
  address: string;
  tag?: string | null;
  network?: string | null;
  info?: unknown;
}

/* ---------- market watcher ---------- */

export interface MarketWatcher extends EntitaBase {
  asset: string;
  currency: string;
  exchangeId: number;
  isActive: boolean;
  exchange?: Exchange;
}

/* ---------- tradebot ---------- */

export type Dex = 'ZEROX' | 'ONEINCH' | 'VELORA';

/** Le uniche catene note al backend (`OneInchChains`). */
export type ChainId = 1 | 10 | 56 | 137;

export interface Trade extends EntitaBase {
  tradebotId: number;
  entryBalance: number | null;
  exitBalance: number | null;
  buyPrice: number | null;
  buySlippage: number | null;
  sellPrice: number | null;
  sellSlippage: number | null;
  change: number | null;
  buyFee: unknown | null;
  buyOrderData: unknown | null;
  sellFee: unknown | null;
  sellOrderData: unknown | null;
  buyDate: string | null;
  sellDate: string | null;
  comment?: string | null;
}

export interface Tradebot extends EntitaBase {
  isRunning: boolean;
  PID: number | null;
  useRealMoney: boolean;
  usePartialBalance: boolean;

  accountId: number | null;
  walletId: number | null;

  dex: Dex;
  minimumBaseCoinBalance: number | null;
  rechargeQtyBaseCoinBalance: number | null;
  chainId: ChainId | null;
  rpc: string | null;
  gasLimit: number | null;
  swapSlippage: number | null;

  marketWatcherId: number;
  currency: string;
  asset: string;

  strategyName: string;
  strategyParams: Record<string, unknown> | null;
  warmupPeriod: number;
  candleSize: number;

  startDate?: string | null;
  endDate?: string | null;

  telegramToken: string | null;
  telegramPassword: string | null;

  initialAssetPrice: number | null;
  initialValue: number | null;
  initialAsset: number | null;
  initialCurrency: number | null;
  assetBalance: number | null;
  currencyBalance: number | null;
  parkedCurrency: number | null;
  marketChange: number | null;
  strategyChange: number | null;
  finalAssetPrice: number | null;
  finalTotalBalance: number | null;

  /**
   * L'ultima volta che il PROCESSO del bot era vivo, e il prezzo che vedeva in quel momento.
   *
   * Da non confondere con `updatedAt`, che dice l'ultima volta che qualcosa e' CAMBIATO. Sono due
   * domande diverse e prima ne esisteva una sola: l'interfaccia usava `updatedAt` come battito
   * cardiaco e annunciava «il bot non scrive da quattro giorni» per un bot perfettamente vivo che
   * teneva una posizione aperta -- e quindi, per definizione, non cambiava niente. Il bot piu'
   * sano risultava il piu' abbandonato.
   *
   * `null` sui bot fermi e su quelli avviati prima che la colonna esistesse: li' non si afferma
   * niente sulla freschezza, non si scrive zero e non si scrive «vecchio».
   */
  lastSeenAt?: string | null;
  lastSeenPrice?: number | null;

  /**
   * Da quando il rendimento e' misurato: non e' la data di creazione del bot, perche' in mezzo
   * c'e' il warmup. Serve a non mettere il numero sotto un'etichetta sbagliata.
   */
  measuredSince?: string | null;

  failedTransactions: number | null;
  errors: string[] | null;
  subscribers: string[] | null;
  banned: string[] | null;

  /** Relazione eager: ogni GET /tradebots porta con sé tutti i trade. */
  trades?: Trade[];
  wallet?: Wallet;
  account?: Account;
  marketWatcher?: MarketWatcher;
}

/* ---------- wallet ---------- */

export interface Wallet extends EntitaBase {
  name: string;
  /** Presente nella risposta del server: non mostrarlo mai in interfaccia. */
  encryptedPrivateKey?: string;
  address: string | null;
}

export interface RiepilogoSaldoWallet {
  walletId: number;
  address: string | null;
  ok: boolean;
  totalUsd?: number;
  okChains: number;
  totalChains: number;
  chainsWithGasAlert: number[];
  cached: boolean;
  scannedAt?: string;
  error?: string;
}

export interface SaldoNativo {
  symbol: string;
  name: string;
  decimals: number;
  logoURI: string | null;
  /** Stringa a 18 decimali: non convertire con `Number` senza arrotondare. */
  balance: string;
  balanceRaw: string;
  balanceUsd?: number;
}

export interface SaldoToken extends SaldoNativo {
  address: string;
  verified: boolean;
  selected: boolean;
}

export interface AvvisoGas {
  level: 'empty' | 'critical' | 'low';
  swapsCovered: number;
  recommendedMinimum: number;
  hasTokens: boolean;
  message: string;
}

export interface SaldiCatena {
  chainId: number;
  name: string;
  nativeSymbol: string;
  logoURI: string | null;
  explorerUrl: string;
  ok: boolean;
  native: SaldoNativo | null;
  tokens: SaldoToken[];
  totalUsd?: number;
  pricedTokens: number;
  blockNumber?: number;
  rpcUrl?: string;
  scannedTokens?: number;
  method?: 'multicall3' | 'direct';
  durationMs: number;
  warnings: string[];
  error?: string;
  gasAlert?: AvvisoGas;
}

export interface SaldiMulticatena {
  walletId?: number;
  address: string;
  chains: SaldiCatena[];
  totalUsd?: number;
  okChains: number;
  totalChains: number;
  cached: boolean;
  scannedAt: string;
  durationMs: number;
}

export interface SaldiWalletSuCatena {
  address: string;
  chainId: number;
  blockNumber: number;
  native: SaldoNativo;
  tokens: SaldoToken[];
  scannedTokens: number;
  method: 'multicall3' | 'direct';
  rpcUrl: string;
  durationMs: number;
  warnings: string[];
}

export type TipoTransazione =
  | 'SWAP'
  | 'DEPOSITO'
  | 'PRELIEVO'
  | 'TRANSFER'
  | 'APPROVE'
  | 'ALTRO';

export interface TrasferimentoTransazione {
  token: string | null;
  symbol: string;
  decimals: number;
  logoURI: string | null;
  amount: string;
  amountRaw: string;
  counterparty: string;
}

export interface TransazioneWallet {
  hash: string;
  blockNumber: number;
  timestamp?: string;
  type: TipoTransazione;
  direction: 'in' | 'out' | 'self' | 'none';
  from: string;
  to: string | null;
  transfersIn: TrasferimentoTransazione[];
  transfersOut: TrasferimentoTransazione[];
  approvals: { token: string; symbol: string; spender: string; unlimited: boolean }[];
  nativeValue?: string;
  nativeSymbol: string;
  gasFee?: string;
  status?: number;
  counterpartyLabel?: string;
  /** Presente quando la transazione è riconducibile a un trade di un bot. */
  bot?: {
    tradeId: number;
    tradebotId: number;
    strategyName: string | null;
    side: 'BUY' | 'SELL';
    asset: string | null;
    currency: string | null;
  };
  summary: string;
}

export interface AttivitaWallet {
  address: string;
  chainId: number;
  chainName: string;
  nativeSymbol: string;
  explorerUrl: string;
  ok: boolean;
  source: 'rpc' | 'explorer';
  fromBlock?: number;
  toBlock?: number;
  blocksScanned?: number;
  approxHours?: number;
  /** `true` quando la finestra RPC rende l'elenco incompleto per costruzione. */
  partial: boolean;
  transactions: TransazioneWallet[];
  rpcUrl?: string;
  durationMs: number;
  warnings: string[];
  error?: string;
}

export interface PersonalWallet extends EntitaBase {
  name: string;
  type: string;
  /** Cifrato dal browser, come gli altri due: non e' un indirizzo leggibile. */
  address: string;
  /** Testo cifrato dal browser (`sbv1.…` o il vecchio `U2FsdGVkX1…`). Il
   *  server non lo tocca. Si decifra solo dentro la schermata Cassaforte, con
   *  la password dell'utente, e non si registra da nessuna parte. */
  privateKey?: string;
  mnemonic?: string | null;
}

/* ---------- backtest ---------- */

export type StatoBacktest = 'RUNNING' | 'DONE' | 'ERROR';

export interface DrawdownMassimo {
  start: number;
  end: number;
  value: number;
  max: number;
  min: number;
}

export interface RendimentoAnnuale {
  year: string;
  totalBalance: number;
  change: number;
  marketChange: number;
}

export interface TradeBacktest {
  entryBalance: number;
  exitBalance: number;
  buyPrice: number;
  sellPrice: number;
  buyDate: string;
  sellDate: string;
  change: number;
  comment?: string;
}

/**
 * Le colonne che `GET /backtester/search` restituisce davvero. È l'unica forma
 * sicura da usare in lista: la entity completa porta con sé le serie
 * (`trades`, `equityLinePoints`, `candlesClosePrices`) e pesa megabyte a riga.
 */
export interface BacktestRiga {
  id: number;
  strategyName: string;
  strategyParams: Record<string, unknown> | null;
  asset: string;
  currency: string;
  candleSize: number;
  warmupPeriod: number;
  startDate: string;
  endDate: string;
  strategyChange: number | null;
  marketChange: number | null;
  drawdownMaxValue: number | null;
  tradesNumber: number;
  winTradesPercentage: number | null;
  cagr: number | null;
  calmar: number | null;
  sharpe: number | null;
  sortino: number | null;
  profitFactor: number | null;
  timeInMarketPct: number | null;
  isFavorite: boolean;
  backtestGroupId: number | null;
  createdAt: string;
  /** La "Tenuta": rapporto fra il rendimento recente e quello precedente. */
  walkForwardScore: number | null;
  /** Come è stato diviso il periodo. 'ciclo' = halving, 'meta' = a metà. */
  walkForwardMetodo: 'ciclo' | 'meta' | null;
  /** Il "Nei ribassi": media nei soli anni in cui il mercato ha perso. */
  bearYearsAvg: number | null;
  bearYearsCount: number | null;
  intorno: number | null;
  intornoParametro: string | null;
  intornoAssi: number | null;
  /**
   * Rendimento anno per anno, col rendimento del mercato accanto.
   *
   * Sta anche nella lista e non solo nel dettaglio: e' l'informazione che nessun totale
   * riassume -- che nel 2022, con il mercato a meno sessanta, una configurazione ha fatto piu'
   * dodici -- e si cerca mentre si scorre. Il server ne manda i soli tre campi utili,
   * quattrocento byte a riga; le serie di operazioni e prezzi, che sono megabyte, restano
   * fuori dalla proiezione della lista.
   */
  yearlyChange: RendimentoAnnuale[] | null;
}

/** La entity completa, servita solo da `GET /backtester/:id`. */
export interface Backtest extends BacktestRiga, EntitaBase {
  exchangeId: number;
  traderParams: Record<string, unknown> | null;
  status: StatoBacktest | null;

  initialAssetPrice: number;
  initialValue: number;
  initialAsset: number;
  initialCurrency: number;

  finalAssetPrice: number | null;
  finalTotalBalance: number | null;
  winTradesNumber: number;
  drawdownMax: DrawdownMassimo | null;
  yearlyChange: RendimentoAnnuale[] | null;

  trades: TradeBacktest[] | null;
  candlesClosePrices: { close: number; timestamp: number }[] | null;
  equityLinePoints: { timestamp: number; value: number }[] | null;

  stopNotes: string | null;
  wasStoppedEarly: boolean;
  warmupWarning: string | null;
  description: string | null;

  expectancyPerTrade: number | null;
  recoveryFactor: number | null;
  ulcerIndex: number | null;
  ulcerPerformanceIndex: number | null;
  maxConsecutiveWins: number | null;
  maxConsecutiveLosses: number | null;
  winLossRatio: number | null;
  avgTradeDurationMinutes: number | null;
  medianTradeDurationMinutes: number | null;
  feeTotal: number | null;
  slippageTotal: number | null;
  alphaVsMarket: number | null;
  informationRatio: number | null;
  trackingError: number | null;
  beta: number | null;
  monthlyChange: Record<string, number> | null;
  bestMonthChange: number | null;
  worstMonthChange: number | null;
  equitySlope: number | null;
  equityRSquared: number | null;
  maxDrawdownDurationDays: number | null;
  averageRecoveryDays: number | null;

  backtestGroup?: BacktestGroup;
}

export interface BacktestGroup extends EntitaBase {
  isRunning: boolean;
  exchangeId: number;
  currency: string;
  asset: string;
  strategyName: string;
  strategyParams: Record<string, unknown> | null;
  timeframeArray: { warmupPeriod?: number; candleSize?: number }[] | null;
  traderParams: Record<string, unknown> | null;
  startDate?: string;
  endDate?: string;
  backtestNumber: number | null;
  initialAssetPrice?: number | null;
  finalAssetPrice?: number | null;
  marketChange?: number | null;
  maxDrawdown?: number | null;
  minWinRate?: number | null;
  maxUnderperformance?: number | null;
  maxTradesPerDay?: number | null;
  earlyStopCheckpoint?: number | null;
  backtests?: Backtest[];
}

export interface StrategiaDisponibile {
  name: string;
  defaultParams: Record<string, unknown>;
  descrizione: string | null;
}

/** Chiavi ordinabili accettate da `GET /backtester/search`. Fuori lista il
 *  server ricade su `strategyChange` in silenzio, quindi non si improvvisa. */
export const CHIAVI_ORDINAMENTO = [
  'strategyChange',
  'drawdownMaxValue',
  'tradesNumber',
  'cagr',
  'calmar',
  'sharpe',
  'sortino',
  'profitFactor',
  'winTradesPercentage',
  'id',
  'createdAt',
  'walkForwardScore',
  'bearYearsAvg',
  'intorno',
] as const;

export type ChiaveOrdinamento = (typeof CHIAVI_ORDINAMENTO)[number];
export type VersoOrdinamento = 'ASC' | 'DESC';

export interface FiltriRicercaBacktest {
  page?: number;
  /** Il server taglia a 200. */
  limit?: number;
  strategyName?: string;
  asset?: string;
  candleSize?: number;
  /** Semantica di COPERTURA: seleziona i backtest che coprono l'intervallo. */
  startDate?: string;
  endDate?: string;
  backtestGroupId?: number;
  minTrades?: number;
  minChange?: number;
  maxDrawdown?: number;
  minIntorno?: number;
  /** Tenuta minima in PERCENTUALE: il server la converte nel rapporto che ha a database. */
  minTenuta?: number;
  sortBy?: /* piu' criteri separati da virgola: `profitto,tenuta` */ string;
  sortDir?: string;
}

export interface RisultatoRicercaBacktest {
  data: BacktestRiga[];
  total: number;
  page: number;
  limit: number;
  pageCount: number;
}

export interface FaccetteBacktest {
  /** `n` arriva come STRINGA: viene da un COUNT(*) grezzo. */
  strategie: { v: string; n: string }[];
  periodi: { dal: string; al: string };
  timeframe: { v: number; n: string }[];
  assets: { v: string; n: string }[];
  gruppi: Pick<
    BacktestGroup,
    'id' | 'strategyName' | 'asset' | 'startDate' | 'endDate' | 'timeframeArray' | 'strategyParams' | 'backtestNumber'
  >[];
}

export interface AvvioBacktest {
  exchangeId: number;
  asset: string;
  currency: string;
  startDate: string;
  endDate: string;
  strategyName: string;
  strategyParams: Record<string, unknown>;
  timeframeArray?: { warmupPeriod?: number; candleSize?: number }[];
  warmupPeriod: number;
  candleSize: number;
  assetBalance: number;
  currencyBalance: number;
  feeMode: 'MAKER' | 'TAKER';
  /** In PERCENTUALE: il server divide per 100. */
  makerFee?: number;
  takerFee?: number;
  slippage?: number;
  /** Senza `force` il server restituisce un backtest identico già esistente. */
  force?: boolean;
}

export interface AvvioGruppoBacktest {
  exchangeId: number;
  asset: string;
  currency: string;
  startDate: string;
  endDate: string;
  strategyName: string;
  strategyParams: Record<string, unknown>;
  timeframeArray: { warmupPeriod?: number; candleSize?: number }[];
  maxDrawdown?: number;
  minWinRate?: number;
  maxUnderperformance?: number;
  maxTradesPerDay?: number;
  earlyStopCheckpoint?: number;
}

/* ---------- dati storici ---------- */

export interface RangeDatiStorici {
  /** Indice progressivo della cache, NON un id di database. */
  id: number;
  exchangeId: number;
  begin: string;
  end: string;
  asset: string;
  currency: string;
  exchange: Exchange;
}

export interface StatoSchemaDatiStorici {
  ranges: number;
  updatedAt: string | null;
  refreshing: boolean;
}

export interface Candela {
  id: string;
  asset: string;
  currency: string;
  timestamp: number;
  humanDate: string;
  exchangeId: number;
  open: number | null;
  high: number | null;
  low: number | null;
  close: number | null;
  volume: number | null;
}

/* ---------- token e catene ---------- */

export interface SuggerimentoGas {
  chainId: number;
  nativeSymbol: string;
  gasLimit: number;
  swapGasUnits: number;
  minimumBaseCoinBalance: number;
  rechargeQtyBaseCoinBalance: number;
  costPerSwap: number;
  costPerSwapWithMargin: number;
  gasPriceWei: string;
  gasPriceGwei: number;
  gasPriceSource: 'rpc' | 'reference';
  referenceMeasuredAt: string;
  spikeMultiplier: number;
  swapsForMinimum: number;
  swapsForRecharge: number;
  notes: string;
  rationale: string;
}

export interface Catena {
  chainId: number;
  name: string;
  nativeSymbol: string;
  logoURI: string;
  explorerUrl: string;
  rpcs: { url: string; label: string }[];
  gasDefaults: SuggerimentoGas | null;
}

export interface Token {
  address: string;
  symbol: string;
  name: string;
  decimals: number;
  logoURI: string | null;
  /** `false` solo quando il dato è letto dal contratto e non da una token list. */
  verified: boolean;
}

export interface EsitoProvaRpc {
  url: string;
  ok: boolean;
  chainId: number | null;
  expectedChainId: number;
  chainIdMatches: boolean;
  latencyMs: number | null;
  blockNumber: number | null;
  gasPriceWei: string | null;
  gasSuggestion: SuggerimentoGas | null;
  error: string | null;
  /** Già in italiano, pronto da mostrare accanto al campo. */
  message: string;
}

export interface PreventivoDex {
  available: boolean;
  price?: number;
  expectedSlippage?: number;
  sources?: string[];
  error?: string;
}

export interface PuntoProfondita {
  amountUsd: number;
  provider: Dex;
  pricePerUnit: number | null;
  impactPct: number | null;
  available: boolean;
  sources?: string[];
  error?: string;
  configured?: boolean;
  reference?: boolean;
}

export interface EsitoLiquidita {
  zerox: PreventivoDex;
  oneinch: PreventivoDex;
  velora: PreventivoDex;
  recommendation: Dex | null;
  warnings: string[];
  priceReference: {
    marketWatcher: {
      price: number;
      timestamp: string;
      ageMinutes: number;
      asset: string;
      currency: string;
      exchange: string;
    } | null;
    cexLive: { price: number; timestamp: string } | null;
    dexImplied: {
      price: number;
      provider: Dex;
      base: string;
      quote: string;
      inverted: boolean;
    } | null;
    deltaCandleVsLive: number | null;
    deltaCexVsDex: number | null;
    deltaCexVsDexBasis: 'live' | 'candle' | null;
    warnings: string[];
  };
  /** Presente solo se richiesto: costa fino a 7 preventivi extra. */
  depthCurve?: {
    provider: Dex | null;
    thresholdPct: number;
    configuredAmountUsd: number;
    points: PuntoProfondita[];
    usableUpTo: { amountUsd: number; impactPct: number } | null;
    firstBreach: {
      amountUsd: number;
      impactPct: number | null;
      reason: 'impact' | 'liquidity' | 'provider';
    } | null;
    quotesExecuted: number;
    note: string;
    warnings: string[];
  };
}
